import { BaseContentResponse } from './content-response';

export interface SearchResponse {
  Results: SearchRecord[];
  Count: number;
  PageIndex: number;
  PageSize: number;
  SearchFacets: SearchFacetResult[] | null;
}

export interface SearchFacetResult {
  Id: string;
  Name: string;
  RecordCount: number;
}

export interface BaseSearchRecord extends BaseContentResponse {
  ContentUrl: string;

  FriendlyTitle: string;
  DisplayDate: string;
  SiteId: string;

  AverageRating: number;
  RatingCount: number;
  ReviewCount: number;

  IntPrepTime?: number | null;
  IntTotalTime?: number | null;
  TotalTime?: string | null;
  PrepTime?: string | null;
}

export type SearchRecord =
  | SearchRecordRecipe
  | SearchRecordProduct
  | SearchRecordArticle
  | SearchRecordProductCategory;

export interface SearchRecordRecipe extends BaseSearchRecord {
  ContentType: 'Recipe';
  AverageRating: number;
  RatingCount: number;
  ReviewCount: number;
  IntPrepTime: number;
  IntTotalTime: number;
  TotalTime: string;
  PrepTime: string;
  ServingNumber: string;
  KitchenTested: boolean;
  NumberOfIngredients: string;
  Description: string;
}

export interface SearchRecordProduct extends BaseSearchRecord {
  ContentType: 'Product';
  AverageRating: number;
  RatingCount: number;
  ReviewCount: number;
  Description: string;
}

export interface SearchRecordArticle extends BaseSearchRecord {
  ContentType: 'Article';
}

export interface SearchRecordProductCategory extends BaseSearchRecord {
  ContentType: 'ProductCategory';
}

const collapseAnyDuplicateLocalePrefix = (path: string) => {
  return path.replace(/^\/([a-z]{2}-[a-z]{2})(?:\/\1)+(?=\/|$)/i, '/$1');
};

const normalizeSearchResultUrl = (url?: string) => {
  if (!url) {
    return url;
  }

  const trimmedUrl = url.trim();
  if (!trimmedUrl || trimmedUrl.startsWith('/api/')) {
    return url;
  }

  if (/^[a-z]{2}-[a-z]{2}(?=\/|$)/i.test(trimmedUrl)) {
    return collapseAnyDuplicateLocalePrefix(`/${trimmedUrl}`);
  }

  if (trimmedUrl.startsWith('/')) {
    return collapseAnyDuplicateLocalePrefix(trimmedUrl);
  }

  try {
    const parsedUrl = new URL(trimmedUrl);
    const originalPath = `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
    const normalizedPath = collapseAnyDuplicateLocalePrefix(originalPath);

    if (normalizedPath !== originalPath) {
      return `${parsedUrl.origin}${normalizedPath}`;
    }
  } catch {
    return url;
  }

  return url;
};

export const normalizeSearchResponseUrls = (searchResponse: SearchResponse): SearchResponse => {
  return {
    ...searchResponse,
    Results: searchResponse.Results.map((result) => ({
      ...result,
      ItemUrl: normalizeSearchResultUrl(result.ItemUrl) ?? result.ItemUrl,
      PageUrl: normalizeSearchResultUrl(result.PageUrl) ?? result.PageUrl,
    })),
  };
};
